import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, Fragment as _Fragment, createElementBlock as _createElementBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-0e9f7f62"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "registering-i18n" }

import { ref, computed, onBeforeMount, onMounted } from "vue";
import { Store } from "vuex";
import { index } from "@/store/index";
import { requiredInject } from "@/util/vue";
import { BackendKey } from "@/backend";
import router from "@/router";
import { AppState } from "@/store/app-state";
import CustomInfoNoteConfirmation from "@/components/CustomInfoNoteConfirmation.vue";
import RegisteringI18nPreviousTreatments from "@/components/RegisteringI18nPreviousTreatments.vue";
import RegisteringI18nCheckPatientId from "@/components/RegisteringI18nCheckPatientId.vue";
import RegisteringI18nRegisteringForm from "@/components/RegisteringI18nRegisteringForm.vue";
import CustomSpinner from "@/components/CustomSpinner.vue";

enum I18N_PHASE {
  INITIAL,
  ASK_USER_IS_CLIENT,
  ASK_PATIENT_ID,
  REGISTER_PATIENT,
  ADDITIONAL_TREATMENTS
}


export default /*#__PURE__*/_defineComponent({
  __name: 'RegisteringI18n',
  setup(__props) {

// TODO carry over already given email and phone from international customer would be nice
const store: Store<unknown> = index;

const backend = requiredInject(BackendKey);

const inProgress = ref<boolean>(true);

/**
 * this glob dictates which page in registration should be shown
 */
const i18nRegisteringPhase = ref<I18N_PHASE>();

const showPatientFoundNotification = ref<boolean>(false);

const commonNoteVisible = computed<boolean>(() => {
  return showPatientFoundNotification.value;
});

/**
 * Is user already registered in MyDocrates?
 * In i18n registration user can't be registered if not found in Acute
 */
const userFoundInMyDocrates = computed<boolean>(() => {
  // pay attention how MD registration status is determined by consent..
  return index.getters.getConsent != null;
});

/**
 * Is user already as a patient in Acute?
 */
const userFoundInAcute = computed<boolean>(() => {
  // ..and Acute registration status is determined by clientId
  return index.getters.getUserClientId;
});

onBeforeMount(async () => {
  if (userFoundInMyDocrates.value && userFoundInAcute.value) {
    // this user is already registered in MyDocrates and Acute
    // actually, we should not even be here
    console.warn(
      "User is already registered in MyDocrates and Acute, redirect to home"
    );
    // redirect already registered user elsewhere
    store.commit("SET_APP_STATE", AppState.ServiceCategories);
    await router.push("/service-categories");
  } else {
    if (store.getters.isAcuteActive) {
      i18nRegisteringPhase.value = I18N_PHASE.ASK_USER_IS_CLIENT;
    }
    else {
      i18nRegisteringPhase.value = I18N_PHASE.REGISTER_PATIENT;
    }
  }
});

onMounted(() => {
  setInProgress(false);
});

function setInProgress(value: boolean): void {
  inProgress.value = value;
}

function scrollToTop(): void {
  if (window?.scrollTo) {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }
}

async function cancelRegisteringProcess(): Promise<void> {
  i18nRegisteringPhase.value = I18N_PHASE.INITIAL;
  store.commit("SET_APP_STATE", AppState.Logout);
  await router.replace("/logout");
}

async function unauthorizedLogout(): Promise<void> {
  i18nRegisteringPhase.value = I18N_PHASE.INITIAL;
  store.commit("SET_APP_STATE", AppState.Logout);
  await router.replace("/logout?expired");
}

function proceedToAskPatientId(): void {
  showPatientFoundNotification.value = false;
  i18nRegisteringPhase.value = I18N_PHASE.ASK_PATIENT_ID;
  scrollToTop();
}

async function handlePreviousTreatmentsSelected(
  havePreviousTreatments: boolean
) {
  if (store.getters.isAcuteActive) {
    if (havePreviousTreatments) {
      i18nRegisteringPhase.value = I18N_PHASE.ASK_PATIENT_ID;
      scrollToTop();
    } else {
      // check if backend found one or more patients having temporary pic
      // with surname, birthday and gender
      // calculated from signicat login information
      setInProgress(true);
      backend
        .checkClientExistence()
        .then(resp => {
          setInProgress(false);
          if (resp.clientExists) {
            // client or similar client found in Acute
            console.debug("clientExists");
            showPatientFoundNotification.value = true;
          } else {
            // we will proceed to register patient
            console.debug("client does not exist, continue");
            i18nRegisteringPhase.value = I18N_PHASE.REGISTER_PATIENT;
          }
        })
        .catch(err => {
          setInProgress(false);
          console.error("checkClientExistence error", err);
        });
    }
  }
  else {
    // if Acute is not active, we will proceed to register patient
    i18nRegisteringPhase.value = I18N_PHASE.REGISTER_PATIENT;
  }
}

function patientIdInserted() {
  i18nRegisteringPhase.value = I18N_PHASE.REGISTER_PATIENT;
  scrollToTop();
}

/**
 * Used here and in RegisteringI18nRegisteringForm via emit
 */
async function continueToTreatmentHistory(): Promise<void> {
  i18nRegisteringPhase.value = I18N_PHASE.ADDITIONAL_TREATMENTS;
  store.commit("SET_APP_STATE", AppState.AdditionalTreatmentInfo);
  await router.replace("/treatments");
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(CustomSpinner, { loading: inProgress.value }, null, 8, ["loading"]),
    (showPatientFoundNotification.value)
      ? (_openBlock(), _createBlock(CustomInfoNoteConfirmation, {
          key: 0,
          msg: "COMMON-CONFIRMATION",
          title: _unref(store).getters.translate('registering-i18n-id-required'),
          header: _unref(store).getters.translate('registering-i18n-id-required-sub'),
          text1: _unref(store).getters.translate('registering-i18n-id-required-description'),
          button1: _unref(store).getters.translate('registering-i18n-input-id'),
          button2: _unref(store).getters.translate('abort-registering'),
          onCloseNote: proceedToAskPatientId,
          onCancelOrChange: cancelRegisteringProcess
        }, null, 8, ["title", "header", "text1", "button1", "button2"]))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_1, [
      (
        !commonNoteVisible.value &&
          i18nRegisteringPhase.value === I18N_PHASE.ASK_USER_IS_CLIENT
      )
        ? (_openBlock(), _createBlock(RegisteringI18nPreviousTreatments, {
            key: 0,
            onCancelRegisteringProcess: cancelRegisteringProcess,
            onPreviousTreatmentsSelected: handlePreviousTreatmentsSelected
          }))
        : _createCommentVNode("", true),
      (
        !commonNoteVisible.value && i18nRegisteringPhase.value === I18N_PHASE.ASK_PATIENT_ID
      )
        ? (_openBlock(), _createBlock(RegisteringI18nCheckPatientId, {
            key: 1,
            onCancelRegisteringProcess: cancelRegisteringProcess,
            onPatientIdInserted: patientIdInserted,
            onShowSpinner: setInProgress
          }))
        : _createCommentVNode("", true),
      (
        !commonNoteVisible.value &&
          i18nRegisteringPhase.value === I18N_PHASE.REGISTER_PATIENT
      )
        ? (_openBlock(), _createBlock(RegisteringI18nRegisteringForm, {
            key: 2,
            onShowSpinner: setInProgress,
            onCancelRegisteringProcess: cancelRegisteringProcess,
            onUnauthorizedLogout: unauthorizedLogout,
            onContinueToTreatmentHistory: continueToTreatmentHistory
          }))
        : _createCommentVNode("", true)
    ])
  ], 64))
}
}

})